import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from "@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, ShieldAlert, ShieldCheck, Mail, User, KeyRound, Loader2 } from "lucide-react";

interface AdminProfile {
    id: string;
    user_id: string;
    email: string;
    display_name: string;
    admin_role: 'admin' | 'super_admin';
    permissions: Record<string, boolean>;
}

const PERMISSION_KEYS = [
    { key: 'delete_bookings', label: 'Delete Bookings' },
    { key: 'delete_salespeople', label: 'Delete Salespeople' },
    { key: 'delete_promo_codes', label: 'Delete Promo Codes' },
    { key: 'delete_referral_codes', label: 'Delete Referral Codes' },
    { key: 'delete_services', label: 'Delete Services' },
    { key: 'delete_categories', label: 'Delete Categories' },
    { key: 'delete_portfolio', label: 'Delete Portfolio' },
    { key: 'delete_reviews', label: 'Delete Reviews' },
    { key: 'delete_hero', label: 'Delete Hero' },
    { key: 'delete_social', label: 'Delete Social' },
    { key: 'delete_messages', label: 'Delete Messages' },
];

export const AdminsManager = () => {
    const [admins, setAdmins] = useState<AdminProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const { user: currentUser, role: userRole } = useAuth();
    const { toast } = useToast();

    // Password reset state
    const [resetPasswordDialog, setResetPasswordDialog] = useState<{ open: boolean; userId: string; email: string }>({
        open: false,
        userId: '',
        email: ''
    });
    const [newPassword, setNewPassword] = useState('');
    const [resettingPassword, setResettingPassword] = useState(false);

    useEffect(() => {
        fetchAdmins();
    }, []);

    const fetchAdmins = async () => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('id, user_id, email, display_name, admin_role, permissions')
                .order('email');

            if (error) throw error;
            setAdmins((data as any) as AdminProfile[]);
        } catch (error: any) {
            toast({
                title: "Error fetching admins",
                description: error.message,
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    const updateRole = async (adminId: string, newRole: 'admin' | 'super_admin') => {
        try {
            const { error } = await supabase
                .from('profiles')
                .update({ admin_role: newRole } as any)
                .eq('id', adminId);

            if (error) throw error;

            setAdmins(admins.map(a => a.id === adminId ? { ...a, admin_role: newRole } : a));
            toast({ title: "Role updated successfully" });
        } catch (error: any) {
            toast({
                title: "Error updating admin_role",
                description: error.message,
                variant: "destructive",
            });
        }
    };

    const togglePermission = async (adminId: string, permKey: string, currentVal: boolean) => {
        const admin = admins.find(a => a.id === adminId);
        if (!admin) return;

        const newPermissions = { ...admin.permissions, [permKey]: !currentVal };

        try {
            const { error } = await supabase
                .from('profiles')
                .update({ permissions: newPermissions } as any)
                .eq('id', adminId);

            if (error) throw error;

            setAdmins(admins.map(a => a.id === adminId ? { ...a, permissions: newPermissions } : a));
            toast({ title: "Permission updated" });
        } catch (error: any) {
            toast({
                title: "Error updating permission",
                description: error.message,
                variant: "destructive",
            });
        }
    };

    const handleResetPassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            toast({
                title: "Invalid password",
                description: "Password must be at least 6 characters",
                variant: "destructive",
            });
            return;
        }

        setResettingPassword(true);
        try {
            // Get the user_id from the admin profile
            const admin = admins.find(a => a.id === resetPasswordDialog.userId);
            if (!admin?.user_id) {
                throw new Error("User ID not found");
            }

            // Use Supabase Admin API to update user password
            const { error } = await supabase.auth.admin.updateUserById(
                admin.user_id,
                { password: newPassword }
            );

            if (error) throw error;

            toast({
                title: "Password reset successful",
                description: `Password updated for ${resetPasswordDialog.email}`,
            });

            setResetPasswordDialog({ open: false, userId: '', email: '' });
            setNewPassword('');
        } catch (error: any) {
            toast({
                title: "Error resetting password",
                description: error.message,
                variant: "destructive",
            });
        } finally {
            setResettingPassword(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-muted-foreground">Loading admin profiles...</div>;

    if (userRole !== 'super_admin') {
        return (
            <Card className="border-destructive/20 bg-destructive/5">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-destructive">
                        <ShieldAlert className="h-5 w-5" /> Access Denied
                    </CardTitle>
                    <CardDescription>
                        Only Super Admins can manage administrator permissions and roles.
                    </CardDescription>
                </CardHeader>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold">Admin Management</h2>
                    <p className="text-muted-foreground">Manage roles and granular permissions for all administrators.</p>
                </div>
            </div>

            <div className="grid gap-6">
                {admins.map((admin) => (
                    <Card key={admin.id} className={admin.email === currentUser?.email ? "border-primary/50 shadow-md" : ""}>
                        <CardHeader className="pb-3 px-6 pt-6">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-muted rounded-full">
                                        <User className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <CardTitle className="text-lg flex items-center gap-2">
                                            {admin.display_name || 'Anonymous Admin'}
                                            {admin.email === currentUser?.email && (
                                                <Badge variant="secondary" className="text-[10px] uppercase font-bold tracking-wider">You</Badge>
                                            )}
                                        </CardTitle>
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-0.5">
                                            <Mail className="h-3 w-3" />
                                            {admin.email}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="flex flex-col items-end gap-1">
                                        <span className="text-[10px] font-bold uppercase text-muted-foreground tracking-tighter">Role</span>
                                        <Select
                                            value={admin.admin_role}
                                            onValueChange={(val: any) => updateRole(admin.id, val)}
                                            disabled={admin.email === currentUser?.email}
                                        >
                                            <SelectTrigger className="w-[140px] h-9">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="admin">
                                                    <div className="flex items-center gap-2">
                                                        <Shield className="h-3.5 w-3.5 text-blue-500" />
                                                        <span>Admin</span>
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="super_admin">
                                                    <div className="flex items-center gap-2">
                                                        <ShieldCheck className="h-3.5 w-3.5 text-orange-500" />
                                                        <span>Super Admin</span>
                                                    </div>
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {admin.email !== currentUser?.email && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setResetPasswordDialog({ open: true, userId: admin.id, email: admin.email })}
                                            className="h-9"
                                        >
                                            <KeyRound className="h-3.5 w-3.5 mr-2" />
                                            Reset Password
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="px-6 pb-6 pt-2">
                            <div className="border-t pt-4">
                                <h4 className="text-xs font-bold uppercase text-muted-foreground tracking-wider mb-4 flex items-center gap-2">
                                    <ShieldAlert className="h-3.5 w-3.5" /> Granular Delete Permissions
                                </h4>

                                {admin.admin_role === 'super_admin' ? (
                                    <div className="bg-orange-50 border border-orange-100 p-4 rounded-lg flex items-start gap-3">
                                        <ShieldCheck className="h-5 w-5 text-orange-500 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-semibold text-orange-900">Full Access Granted</p>
                                            <p className="text-xs text-orange-700">As a Super Admin, this user has all permissions by default. Individual toggles are disabled.</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                        {PERMISSION_KEYS.map((perm) => (
                                            <div key={perm.key} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-transparent hover:border-border transition-colors">
                                                <span className="text-sm font-medium">{perm.label}</span>
                                                <Switch
                                                    checked={!!admin.permissions[perm.key]}
                                                    onCheckedChange={() => togglePermission(admin.id, perm.key, !!admin.permissions[perm.key])}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Dialog open={resetPasswordDialog.open} onOpenChange={(open) => !resettingPassword && setResetPasswordDialog({ ...resetPasswordDialog, open })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Reset Password</DialogTitle>
                        <DialogDescription>
                            Set a new password for {resetPasswordDialog.email}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="new-password">New Password</Label>
                            <Input
                                id="new-password"
                                type="password"
                                placeholder="Enter new password (min 6 characters)"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                disabled={resettingPassword}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setResetPasswordDialog({ open: false, userId: '', email: '' });
                                setNewPassword('');
                            }}
                            disabled={resettingPassword}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleResetPassword}
                            disabled={resettingPassword || !newPassword}
                        >
                            {resettingPassword && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Reset Password
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
